(() => {
  'use strict';

  if (customElements.get('rossanti-fit-guide')) return;

  function chartRows(config) {
    if (
      !config ||
      config.verified !== true ||
      !Array.isArray(config.rows) ||
      typeof config.size_system !== 'string' ||
      !config.size_system.trim()
    ) {
      return [];
    }

    const valid = config.rows.every(row =>
      row &&
      typeof row.size === 'string' &&
      row.size.trim() &&
      typeof row.min_cm === 'number' &&
      Number.isFinite(row.min_cm) &&
      row.min_cm > 0 &&
      typeof row.max_cm === 'number' &&
      Number.isFinite(row.max_cm) &&
      row.max_cm >= row.min_cm
    );

    return valid ? config.rows : [];
  }

  function publishedRows(guide) {
    if (!guide) return [];

    for (const table of guide.querySelectorAll('table')) {
      const trs = [...table.querySelectorAll('tr')];

      if (!trs.length) continue;

      const headers = [...trs[0].querySelectorAll('th,td')]
        .map(cell => cell.textContent.trim());

      const us = headers.findIndex(text =>
        /^US\s+Size$/i.test(text)
      );

      const length = headers.findIndex(text =>
        /^Foot\s+Length\s*\(mm\)$/i.test(text)
      );

      if (us < 0 || length < 0) continue;

      const rows = [];

      for (const tr of trs.slice(1)) {
        const cells = [...tr.querySelectorAll('th,td')];
        const size = cells[us]?.textContent.trim();
        const measurement = cells[length]?.textContent.trim();

        const match = measurement?.match(
          /^(\d+(?:\.\d+)?)\s*(?:[–—-]\s*(\d+(?:\.\d+)?))?$/
        );

        if (!size || !match) return [];

        const min = Number(match[1]) / 10;
        const max = Number(match[2] || match[1]) / 10;

        if (min <= 0 || max < min) return [];

        rows.push({
          size,
          min_cm: min,
          max_cm: max
        });
      }

      return rows;
    }

    return [];
  }

  class RossantiFitGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.unit = 'cm';

      try {
        this.config = JSON.parse(this.dataset.config || 'null');
      } catch {
        this.config = null;
      }

      this.dialog = this.querySelector('dialog');

      const guide = this.queryDialog(
        '[data-rfg-existing-guide]'
      );

      this.rows = chartRows(this.config);
      this.productSpecific = this.rows.length > 0;

      if (!this.productSpecific) {
        this.rows = publishedRows(guide);
      }

      this.sizeSystem = this.productSpecific
        ? this.config.size_system
        : 'US Men';

      this.queryDialog('[data-rfg-product-chart]').hidden =
        !!guide && !this.productSpecific;

      this.originalParent = this.dialog.parentNode;

      this.querySelectorAll('[data-rfg-open]').forEach(button => {
        button.addEventListener('click', () => {
          this.openPanel(button.dataset.rfgOpen, button);
        });
      });

      this.dialog.addEventListener('click', event => {
        const button = event.target.closest('button');

        if (button?.hasAttribute('data-rfg-close')) {
          this.dialog.close();
        }

        if (button?.dataset.rfgUnit) {
          this.setUnit(button.dataset.rfgUnit);
        }

        if (button?.dataset.rfgTab) {
          this.setTab(button.dataset.rfgTab);
        }

        if (button?.hasAttribute('data-rfg-find')) {
          this.findSize();
        }

        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();

          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          ) {
            this.dialog.close();
          }
        }
      });

      this.dialog.addEventListener('close', () => {
        this.onClose();
      });

      this.dialog.addEventListener('input', event => {
        if (event.target.matches('[data-rfg-length]')) {
          this.canonicalCm = undefined;
        }

        this.clearResult();
      });

      this.dialog.addEventListener('change', () => {
        this.clearResult();
      });

      this.dialog.addEventListener('keydown', event => {
        if (
          event.key === 'Enter' &&
          event.target.matches('.rfg-fields input')
        ) {
          event.preventDefault();
          this.findSize();
        }
      });

      this.queryDialog('[data-rfg-find]').textContent =
        this.rows.length
          ? 'Find My Size'
          : 'Prepare Fit Request';

      this.queryDialog('[data-rfg-finder-note]').textContent =
        this.rows.length
          ? 'This tool uses the published foot-length chart as a starting reference. Product fit notes take priority. It does not assess width or instep.'
          : 'An automatic recommendation is not available for this style. Prepare your measurements to ask us for advice.';

      this.queryDialog('[data-rfg-fit-note]').textContent =
        typeof this.config?.fit_note === 'string'
          ? this.config.fit_note
          : '';

      this.renderChart();
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.onClose();
      }
    }

    queryDialog(selector) {
      return this.dialog.querySelector(selector);
    }

    openPanel(panel, trigger) {
      if (this.dialog.open) return;

      this.trigger = trigger;

      const title = panel === 'guide'
        ? 'Size Guide'
        : 'Find Your Fit';

      this.queryDialog('[data-rfg-title]').textContent = title;
      this.dialog.setAttribute('aria-label', title);

      this.dialog
        .querySelectorAll('[data-rfg-panel]')
        .forEach(node => {
          node.hidden = node.dataset.rfgPanel !== panel;
        });

      document.body.appendChild(this.dialog);

      this.previousOverflow =
        document.documentElement.style.overflow;

      document.documentElement.style.overflow = 'hidden';

      try {
        this.dialog.showModal();
      } catch (error) {
        this.onClose();
        throw error;
      }

      this.queryDialog('[data-rfg-title]').focus();
    }

    onClose() {
      document.documentElement.style.overflow =
        this.previousOverflow ?? '';

      this.originalParent.appendChild(this.dialog);

      if (this.trigger?.isConnected) {
        this.trigger.focus();
      }
    }

    setTab(tab) {
      this.dialog
        .querySelectorAll('[data-rfg-tab]')
        .forEach(node => {
          node.setAttribute(
            'aria-pressed',
            String(node.dataset.rfgTab === tab)
          );
        });

      this.dialog
        .querySelectorAll('[data-rfg-view]')
        .forEach(node => {
          node.hidden = node.dataset.rfgView !== tab;
        });
    }

    setUnit(unit) {
      if (unit === this.unit) return;

      const input = this.queryDialog('[data-rfg-length]');
      const value = Number(input.value);

      if (input.value && Number.isFinite(value)) {
        const cm = this.canonicalCm ??
          (this.unit === 'in' ? value * 2.54 : value);

        this.canonicalCm = cm;

        input.value = (
          unit === 'in' ? cm / 2.54 : cm
        )
          .toFixed(4)
          .replace(/0+$/, '')
          .replace(/\.$/, '');
      }

      this.unit = unit;
      input.step = 'any';

      input.placeholder = unit === 'cm'
        ? 'e.g. 26.5'
        : 'e.g. 10.43';

      this.queryDialog('[data-rfg-unit-label]').textContent =
        unit;

      this.dialog
        .querySelectorAll('[data-rfg-unit]')
        .forEach(node => {
          node.setAttribute(
            'aria-pressed',
            String(node.dataset.rfgUnit === unit)
          );
        });

      this.clearResult();
      this.renderChart();
    }

    renderChart() {
      const body = this.queryDialog('[data-rfg-rows]');

      body.replaceChildren();

      this.queryDialog('.rfg-table-wrap').hidden =
        !this.rows.length;

      this.queryDialog('[data-rfg-no-chart]').hidden =
        !!this.rows.length;

      this.queryDialog('[data-rfg-caption]').textContent =
        `${this.sizeSystem} size — ${this.dataset.product}`;

      this.queryDialog(
        '[data-rfg-length-heading]'
      ).textContent = `Foot length (${this.unit})`;

      this.rows.forEach(row => {
        const tr = document.createElement('tr');
        const th = document.createElement('th');
        const td = document.createElement('td');

        th.scope = 'row';
        th.textContent = row.size;

        const display = cm => (
          this.unit === 'in' ? cm / 2.54 : cm
        ).toFixed(2);

        td.textContent =
          `${display(row.min_cm)}–${display(row.max_cm)}`;

        tr.append(th, td);
        body.append(tr);
      });
    }

    clearResult() {
      this.queryDialog('[data-rfg-result]').hidden = true;
      this.queryDialog('[data-rfg-request]').hidden = true;
      this.queryDialog('[data-rfg-error]').textContent = '';
    }

    findSize() {
      this.clearResult();

      const input = this.queryDialog('[data-rfg-length]');

      const rawCm = this.canonicalCm ?? (
        Number(input.value) *
        (this.unit === 'in' ? 2.54 : 1)
      );

      const cm = Math.round(rawCm * 10000) / 10000;

      if (
        !input.value ||
        !Number.isFinite(cm) ||
        cm <= 0
      ) {
        this.queryDialog('[data-rfg-error]').textContent =
          'Enter a positive foot-length measurement, using the selected unit.';

        input.focus();
        return;
      }

      const width = this.queryDialog(
        '[data-rfg-width]'
      ).value;

      const usual = this.queryDialog(
        '[data-rfg-usual]'
      ).value.trim();

      const matches = this.rows.filter(row =>
        cm >= row.min_cm && cm <= row.max_cm
      );

      const result = this.queryDialog('[data-rfg-result]');
      const heading = document.createElement('strong');
      const detail = document.createElement('p');

      if (
        this.rows.length &&
        matches.length === 1 &&
        width === 'standard'
      ) {
        heading.textContent =
          `Reference size: ${this.sizeSystem} ${matches[0].size}`;

        detail.textContent =
          "Based on the published foot-length chart only. Review this style's fit notes and check whether the size is offered for your chosen color. This result does not confirm width, instep or stock availability.";
      } else {
        heading.textContent = 'Let us help confirm your fit';

        if (!this.rows.length) {
          detail.textContent =
            'A verified automatic size recommendation is not available for this style. Share the details below with us.';
        } else if (width !== 'standard') {
          detail.textContent =
            'Length alone cannot confirm your width or instep fit. Share the details below so we can advise on this style.';
        } else {
          detail.textContent =
            'Your measurement is outside a single confirmed range, or falls between sizes. Share the details below for advice.';
        }
      }

      result.replaceChildren(heading, detail);
      result.hidden = false;

      this.queryDialog('[data-rfg-request-text]').value = [
        'Hello ROSSANTI, please help me confirm my size.',
        `Style: ${this.dataset.product}`,
        `Product: ${this.dataset.productUrl}`,
        `Longer foot: ${cm.toFixed(2)} cm (${(cm / 2.54).toFixed(2)} in)`,
        `Usual width / fit: ${width}`,
        `Usual shoe size & brand: ${usual || 'Not provided'}`
      ].join('\n');

      this.queryDialog('[data-rfg-request]').hidden = false;
    }
  }

  customElements.define(
    'rossanti-fit-guide',
    RossantiFitGuide
  );
})();